import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Collapse,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import type { UnapprovedAllocationDto } from '@billda/api';
import { routeBuilders } from '../../../routes';
import { formatCurrency } from '../../../shared/utils/currency';
import { ChipTabs, type ChipTab } from '../../../shared/components/ui/ChipTabs';
import { TreeConnectorBox } from '../../../shared/components/grouped-view';
import { PotStatusBadge } from '../../finance/components/PotStatusBadge';
import { useGetUnapprovedAllocations } from '../hooks/useGetUnapprovedAllocations';
import { formatWeekLabel } from '../utils/periodFormatting';
import {
  filterByType,
  groupByWeekThenPot,
  type AllocationTypeFilter,
  type UnapprovedPotGroup,
  type UnapprovedWeekGroup,
} from '../utils/groupUnapprovedByWeekThenPot';

const TYPE_FILTER_PARAM = 'allocType';
const TYPE_FILTER_TABS: ChipTab[] = [
  { label: 'All', value: 'all' },
  { label: 'Variations', value: 'variations' },
  { label: 'Labour items', value: 'labour' },
];

export function UnapprovedAllocationsWidget() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetUnapprovedAllocations();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsedPotIds, setCollapsedPotIds] = useState<Set<string>>(
    new Set()
  );

  const typeFilter = parseTypeFilter(searchParams.get(TYPE_FILTER_PARAM));

  const weeks = useMemo(() => {
    const allocations = data?.allocations ?? [];
    return groupByWeekThenPot(filterByType(allocations, typeFilter));
  }, [data, typeFilter]);

  const handleTypeFilterChange = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(TYPE_FILTER_PARAM, value);
      return next;
    });
  };

  const handleTogglePot = (potId: string) => {
    setCollapsedPotIds(prev => toggleSetMember(prev, potId));
  };

  const handleRowClick = (allocation: UnapprovedAllocationDto) => {
    navigate(buildAllocationDeepLink(allocation));
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const totalCount = data?.totalCount ?? 0;

  return (
    <Card
      data-testid="unapproved-allocations-widget"
      sx={{ flex: 1, minWidth: 0 }}
    >
      <CardContent
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Unapproved Allocations
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.secondary"
            data-testid="unapproved-allocations-total-count"
          >
            {totalCount}
          </Typography>
        </Box>

        <ChipTabs
          tabs={TYPE_FILTER_TABS}
          value={typeFilter}
          onChange={handleTypeFilterChange}
          data-testid="unapproved-allocations-type-filter"
        />

        <Box
          sx={{ maxHeight: 420, overflowY: 'auto' }}
          data-testid="unapproved-allocations-list"
        >
          {weeks.length === 0 ? (
            <EmptyState />
          ) : (
            <Stack spacing={2}>
              {weeks.map(week => (
                <WeekSection
                  key={`${week.periodYear}-${week.periodWeekNumber}`}
                  week={week}
                  collapsedPotIds={collapsedPotIds}
                  onTogglePot={handleTogglePot}
                  onRowClick={handleRowClick}
                />
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function parseTypeFilter(value: string | null): AllocationTypeFilter {
  if (value === 'variations' || value === 'labour') {
    return value;
  }
  return 'all';
}

function toggleSetMember(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

function buildAllocationDeepLink(allocation: UnapprovedAllocationDto): string {
  const potPath = routeBuilders.finance.potDetail(allocation.potId);
  return `${potPath}?panel=allocationDetails&id=${allocation.allocationId}&siteId=${allocation.siteId}&plotId=${allocation.plotId}`;
}

interface WeekSectionProps {
  week: UnapprovedWeekGroup;
  collapsedPotIds: Set<string>;
  onTogglePot: (potId: string) => void;
  onRowClick: (allocation: UnapprovedAllocationDto) => void;
}

function WeekSection({
  week,
  collapsedPotIds,
  onTogglePot,
  onRowClick,
}: WeekSectionProps) {
  const weekLabel = formatWeekLabel([
    {
      year: week.periodYear,
      weekNumber: week.periodWeekNumber,
      periodStart: week.weekCommencing,
      periodEnd: week.weekCommencing,
    },
  ]);
  const weekCommencingLabel = format(parseISO(week.weekCommencing), 'd MMM');

  return (
    <Box
      data-testid={`unapproved-week-${week.periodYear}-${week.periodWeekNumber}`}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          py: 0.5,
          zIndex: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {weekLabel} · w/c {weekCommencingLabel}
        </Typography>
      </Box>
      <Stack spacing={1}>
        {week.pots.map(pot => (
          <PotGroupSection
            key={pot.potId}
            pot={pot}
            collapsed={collapsedPotIds.has(pot.potId)}
            onToggle={() => onTogglePot(pot.potId)}
            onRowClick={onRowClick}
          />
        ))}
      </Stack>
    </Box>
  );
}

interface PotGroupSectionProps {
  pot: UnapprovedPotGroup;
  collapsed: boolean;
  onToggle: () => void;
  onRowClick: (allocation: UnapprovedAllocationDto) => void;
}

function PotGroupSection({
  pot,
  collapsed,
  onToggle,
  onRowClick,
}: PotGroupSectionProps) {
  return (
    <Box data-testid={`unapproved-pot-group-${pot.potId}`}>
      <ButtonBase
        onClick={onToggle}
        data-testid={`unapproved-pot-group-toggle-${pot.potId}`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 0.5,
          borderRadius: 1,
          textAlign: 'left',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
          }}
        />
        <PotStatusBadge status={pot.potStatus} />
        {pot.isGang ? (
          <GroupsIcon
            sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
          />
        ) : (
          <PersonIcon
            sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
          />
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {pot.claimantName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {pot.unapprovedCount} unapproved
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ minWidth: 80, textAlign: 'right' }}
        >
          {formatCurrency(pot.subtotal)}
        </Typography>
      </ButtonBase>

      <Collapse in={!collapsed} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 0.5 }}>
          {pot.allocations.map((allocation, index) => (
            <AllocationRow
              key={allocation.allocationId}
              allocation={allocation}
              isLast={index === pot.allocations.length - 1}
              onClick={onRowClick}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

interface AllocationRowProps {
  allocation: UnapprovedAllocationDto;
  isLast: boolean;
  onClick: (allocation: UnapprovedAllocationDto) => void;
}

function AllocationRow({ allocation, isLast, onClick }: AllocationRowProps) {
  const waitAge = formatDistanceToNow(parseISO(allocation.bookedAt), {
    addSuffix: true,
  });
  const label = [
    allocation.uniqueReference,
    allocation.workTypeName || 'Unknown work type',
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <ButtonBase
      onClick={() => onClick(allocation)}
      data-testid={`unapproved-allocation-row-${allocation.allocationId}`}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ pl: 1.5 }}>
        <TreeConnectorBox isLast={isLast}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              pr: 1,
              minHeight: 32,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {label}
            </Typography>
            {allocation.resubmissionCount > 0 && (
              <Chip
                label={`resubmitted ×${allocation.resubmissionCount}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.625rem', flexShrink: 0 }}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, minWidth: 72, textAlign: 'right' }}
            >
              {waitAge}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ minWidth: 72, textAlign: 'right', flexShrink: 0 }}
            >
              {formatCurrency(allocation.amountClaimed)}
            </Typography>
          </Box>
        </TreeConnectorBox>
      </Box>
    </ButtonBase>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{ py: 4, textAlign: 'center' }}
      data-testid="unapproved-allocations-empty"
    >
      <Typography variant="body2" color="text.secondary">
        Nothing awaiting approval
      </Typography>
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Card
      data-testid="unapproved-allocations-widget-loading"
      sx={{ flex: 1, minWidth: 0 }}
    >
      <CardContent sx={{ p: 3 }}>
        <Skeleton width={220} height={24} sx={{ mb: 2 }} />
        <Skeleton width="100%" height={32} sx={{ mb: 2 }} />
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
